import {
  ColumnDef,
  RowSelectionState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Alert } from 'components/ui/alert';
import { Checkbox } from 'components/ui/checkbox';
import { DataTable } from 'components/ui/data-table';
import { useChartConfig } from 'hooks/useChartConfig';
import { usePageCarto } from 'hooks/usePageCarto';
import { ApiData } from 'interfaces/api';
import { IndicatorStub } from 'interfaces/indicator';
import { useEffect, useMemo, useState } from 'react';

const buildRowSelection = (ids: number[]): RowSelectionState => {
  return ids.reduce((selection, id) => {
    selection[id.toString()] = true;
    return selection;
  }, {} as RowSelectionState);
};

const areRowSelectionsEqual = (a: RowSelectionState, b: RowSelectionState) => {
  const aKeys = Object.keys(a).filter((key) => a[key]);
  const bKeys = Object.keys(b).filter((key) => b[key]);

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  const aSet = new Set(aKeys);
  return bKeys.every((key) => aSet.has(key));
};

export const IndicatorsForm = () => {
  const { indicators: availableIndicators } = usePageCarto();
  const {
    chart: { indicators: chartIndicators = [] },
    updateChart,
  } = useChartConfig();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    buildRowSelection(chartIndicators)
  );

  useEffect(() => {
    const nextSelection = buildRowSelection(chartIndicators);
    setRowSelection((previousSelection) => {
      if (areRowSelectionsEqual(previousSelection, nextSelection)) {
        return previousSelection;
      }
      return nextSelection;
    });
  }, [chartIndicators]);

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection)
      .filter((key) => rowSelection[key])
      .map(Number)
      .sort((a, b) => a - b);
    const chartIds = [...chartIndicators].sort((a, b) => a - b);

    const hasDifference =
      selectedIds.length !== chartIds.length ||
      selectedIds.some((id, index) => id !== chartIds[index]);

    if (hasDifference) {
      updateChart({
        indicators: selectedIds,
      });
    }
  }, [chartIndicators, rowSelection, updateChart]);

  const columns = useMemo<ColumnDef<ApiData<IndicatorStub>, unknown>[]>(() => {
    return [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                ? 'indeterminate'
                : false
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Sélectionner tous les indicateurs"
            className="mx-auto"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Sélectionner ${row.original.name}`}
            className="mx-auto"
          />
        ),
        enableSorting: false,
        enableHiding: false,
        meta: {
          className: 'w-12 text-center',
          cellClassName: 'text-center',
        },
      },
      {
        accessorKey: 'name',
        header: 'Indicateur',
      },
      {
        accessorKey: 'description',
        header: 'Description',
      },
      {
        accessorKey: 'source',
        header: 'Source',
      },
      {
        accessorKey: 'validity',
        header: 'Validité',
      },
    ];
  }, []);

  const table = useReactTable({
    data: availableIndicators,
    columns,
    state: {
      rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.id.toString(),
    getCoreRowModel: getCoreRowModel(),
  });

  if (availableIndicators.length === 0) {
    return (
      <Alert variant="info">
        Aucun indicateur n&apos;est disponible sur cette page carto.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-slate-600">
        Sélectionnez les indicateurs à afficher dans ce graphique.
      </p>
      <DataTable
        table={table}
        className="max-h-96 overflow-y-auto"
        emptyMessage="Aucun indicateur"
      />
    </div>
  );
};
